import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  Collapse,
  theme,
  Row,
  Alert,
} from "antd";
import type { ButtonProps } from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { useDatasetLoad } from "../../hooks/useDatasetLoad";
import {
  Questionnaire,
  MAX_PAGE_SIZE,
  QuestionnaireQuestionTranslation,
  QuestionnaireQuestionChoiceTranslation,
} from "../../repository/data.default";
import {
  addQuestionnaire,
  deleteQuestionnaire,
  updateQuestionnaire,
  addQuestionnaireSection,
  addQuestionnaireQuestion,
  addQuestionnaireChoice,
  getQuestionnaire,
  updateQuestionnaireSection,
  updateQuestionnaireQuestion,
  updateQuestionnaireQuestionChoice,
  deleteQuestionnaireSection,
  deleteQuestionnaireQuestion,
  deleteQuestionnaireQuestionChoice,
  getLanguages,
  addQuestionnaireQuestionTranslation,
  addQuestionnaireQuestionChoiceTranslation,
  deleteQuestionnaireQuestionTranslation,
  updateQuestionnaireQuestionTranslation,
  deleteQuestionnaireQuestionChoiceTranslation,
  updateQuestionnaireQuestionChoiceTranslation,
} from "../../repository/data.repository";
import {
  fetchQuestionnaireData,
  findDeletedItemsRecursively,
  InitialFormValues,
  prepareChoicesData,
  prepareChoiceTranslations,
  prepareQuestionnaireData,
  prepareQuestionsData,
  prepareQuestionTranslations,
  prepareSectionsData,
  prepareUpdateChoiceData,
  prepareUpdateQuestionData,
  prepareUpdateQuestionnaireData,
  prepareUpdateSectionData,
  QUESTION_TYPE_LABELS,
  QUESTION_TYPES,
  QUESTIONNAIRE_TYPES,
} from "./QuestionnairesView.default";
import { getProblemMessage } from "../../utils/http";

const { Panel } = Collapse;

export interface QuestionnairesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: Questionnaire;
  // A published or retired version is frozen (the server refuses every write to it): shown, never saved.
  readonly readOnly?: boolean;
  readonly onFinish?: (mode?: "update", values?: Questionnaire) => void;
  readonly onCancel?: () => void;
}

// An add / remove control of the form. A read-only (published or retired) version offers none: the form's
// `disabled` does not reach these buttons, and a control that looks live on a frozen version misleads.
function EditButton({ locked, ...rest }: ButtonProps & { readonly locked?: boolean }) {
  return locked ? null : <Button {...rest} />;
}

const versionLabel = (q?: Questionnaire) =>
  q?.version ? ` · v${q.version}${q.status ? ` · ${q.status}` : ""}` : "";

function QuestionnairesFormDialog(props: QuestionnairesFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  // Separate from the submit `loading` so saving never blanks the form; drives the loading gate on open.
  const dataLoad = useDatasetLoad();
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Something went wrong, try again");
  const [languages, setLanguages] = useState<
    Array<{ uid: string; name: string }>
  >([]);
  const initialFormValues = useRef<InitialFormValues | null>(null);
  const [createdQuestionnaire, setCreatedQuestionnaire] =
    useState<Questionnaire | null>(null);

  const [hiddenChoices, setHiddenChoices] = useState<
    {
      sectionName: number;
      questionName: number;
      type: string;
    }[]
  >([]);

  const values = Form.useWatch([], form);

  const reset = useCallback(() => {
    form.resetFields();
    setValid(false);
    setError(false);
  }, [form]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  useEffect(() => {
    if (props.open && props.mode === "update" && props.initialValues?.uid) {
      const uid = props.initialValues.uid;
      dataLoad
        .load<any>([
          () => getQuestionnaire(uid),
          () => fetchQuestionnaireData(uid),
        ])
        .then(([questionnaire, formData]) => {
          const formValues: InitialFormValues = {
            name: questionnaire.name,
            type: questionnaire.type as "Test" | "Survey" | "Roadmap",
            description: questionnaire.description,
            duration: questionnaire.duration,
            ...formData,
          };
          form.setFieldsValue(formValues);
          // Store initial values in ref
          initialFormValues.current = formValues;
        })
        .catch((error) => {
          setErrorMessage(getProblemMessage(error));
          setError(true);
        });
    } else if (props.open) {
      form.setFieldsValue(props.initialValues);
    } else {
      reset();
      initialFormValues.current = null;
    }
  }, [form, props.initialValues, props.mode, props.open, reset]);

  useEffect(() => {
    getLanguages({
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
      searchQuery: { conditions: [] },
    }).then((result) => {
      setLanguages(result.data);
    });
  }, []);

  function onSubmit() {
    form.validateFields().then((values) => {
      setLoading(true);
      setError(false);

      if (props.mode === "add") {
        const questionnaireData = prepareQuestionnaireData(values);

        addQuestionnaire(questionnaireData)
          .then((questionnaire) => {
            setCreatedQuestionnaire(questionnaire);
            const sections = prepareSectionsData(
              values.sections || [],
              questionnaire.uid,
            );
            return Promise.all(
              sections.map((sectionData) =>
                addQuestionnaireSection(sectionData).then((section) => {
                  const sectionIndex = values.sections.findIndex(
                    (s: any) => s.name === sectionData.name,
                  );
                  const questions =
                    values.sections[sectionIndex]?.questions || [];

                  const preparedQuestions = prepareQuestionsData(
                    questions,
                    section.uid,
                  );
                  return Promise.all(
                    preparedQuestions.map((questionData) =>
                      addQuestionnaireQuestion(questionData).then(
                        (question) => {
                          const questionIndex = questions.findIndex(
                            (q: any) =>
                              q.description === questionData.description &&
                              q.questionType === questionData.questionType,
                          );

                          const promises: Promise<any>[] = [];

                          // Handle question translations
                          if (questions[questionIndex].translations) {
                            const questionTranslations =
                              prepareQuestionTranslations(
                                question.uid,
                                questions[questionIndex].translations,
                              );
                            promises.push(
                              Promise.all(
                                questionTranslations.map((data) =>
                                  addQuestionnaireQuestionTranslation(data),
                                ),
                              ),
                            );
                          }

                          // Then handle choices and their translations if they exist
                          if (
                            questionIndex !== -1 &&
                            questions[questionIndex]?.choices
                          ) {
                            const choices = questions[questionIndex].choices;
                            const preparedChoices = prepareChoicesData(
                              choices,
                              question.uid,
                            );

                            return Promise.all(
                              preparedChoices.map((choiceData) =>
                                addQuestionnaireChoice(choiceData),
                              ),
                            ).then((createdChoices) => {
                              // Handle choice translations
                              createdChoices.forEach((choice, idx) => {
                                if (choices[idx].translations) {
                                  const choiceTranslations =
                                    prepareChoiceTranslations(
                                      choice.uid,
                                      choices[idx].translations,
                                    );
                                  promises.push(
                                    Promise.all(
                                      choiceTranslations.map((data) =>
                                        addQuestionnaireQuestionChoiceTranslation(
                                          data,
                                        ),
                                      ),
                                    ),
                                  );
                                }
                              });

                              return Promise.all(promises).then(
                                () => undefined,
                              );
                            });
                          }

                          // If no choices, just handle question translations
                          return Promise.all(promises).then(() => undefined);
                        },
                      ),
                    ),
                  );
                }),
              ),
            );
          })
          .then(() => {
            setCreatedQuestionnaire(null);
            props.onFinish?.();
          })
          .catch((error) => {
            setErrorMessage(getProblemMessage(error));
            setError(true);
            if (createdQuestionnaire?.uid)
              props.onFinish?.("update", createdQuestionnaire);
          })
          .finally(() => {
            setLoading(false);
          });
      } else if (props.mode === "update") {
        form.validateFields().then((values) => {
          setLoading(true);
          setError(false);

          // check for values that change
          const questionnaireData = prepareUpdateQuestionnaireData(
            values,
            props.initialValues!,
          );

          // Find all deleted items
          const deletedItems = findDeletedItemsRecursively(
            initialFormValues.current,
            values,
          );

          // First delete all items in reverse order (choices -> questions -> sections)
          Promise.all([
            ...deletedItems.choices.map((uid) =>
              deleteQuestionnaireQuestionChoice(uid),
            ),
            ...deletedItems.questions.map((uid) =>
              deleteQuestionnaireQuestion(uid),
            ),
            ...deletedItems.sections.map((uid) =>
              deleteQuestionnaireSection(uid),
            ),
          ])
            .then(async () => {
              if (questionnaireData) {
                return updateQuestionnaire(questionnaireData);
              }
              return {} as Questionnaire;
            })
            .then(async () => {
              // Use initialFormValues instead of making new requests
              await Promise.all(
                (values.sections || []).map(async (section: any) => {
                  const existingSection =
                    initialFormValues.current?.sections?.find(
                      (s) => s.uid === section.uid,
                    );

                  if (existingSection) {
                    // Update existing section
                    const sectionData = prepareUpdateSectionData(
                      section,
                      existingSection,
                    );
                    const updatedSection = sectionData
                      ? await updateQuestionnaireSection(sectionData)
                      : existingSection;

                    await Promise.all(
                      (section.questions || []).map(async (question: any) => {
                        const existingQuestion =
                          existingSection.questions?.find(
                            (q) => q.uid === question.uid,
                          );

                        if (existingQuestion) {
                          // Update existing question
                          const questionData = prepareUpdateQuestionData(
                            question,
                            existingQuestion,
                          );
                          const updatedQuestion = questionData
                            ? await updateQuestionnaireQuestion(questionData)
                            : existingQuestion;

                          // Handle question translations
                          if (question.translations) {
                            const existingTranslations =
                              existingQuestion.translations || [];
                            const translationsToDelete =
                              existingTranslations.filter(
                                (t: any) =>
                                  !question.translations.some(
                                    (newT: { language: string }) =>
                                      newT.language === t.language,
                                  ),
                              );

                            await Promise.all(
                              translationsToDelete.map((t) =>
                                deleteQuestionnaireQuestionTranslation(t.uid),
                              ),
                            );

                            // Update or add translations
                            await Promise.all(
                              question.translations.map(
                                async (translation: {
                                  language: string;
                                  text: string;
                                }) => {
                                  const existingTranslation =
                                    existingTranslations.find(
                                      // fix the type
                                      (t: any) =>
                                        t.language === translation.language,
                                    );

                                  if (existingTranslation) {
                                    // Only update if translation text has changed
                                    if (
                                      translation.text !==
                                      existingTranslation.text
                                    ) {
                                      return updateQuestionnaireQuestionTranslation(
                                        {
                                          uid: existingTranslation.uid,
                                          translatedDescription:
                                            translation.text,
                                          questionnaireQuestionUid:
                                            updatedQuestion.uid,
                                          languageUid: translation.language,
                                          rowVersion:
                                            existingTranslation.rowVersion,
                                        },
                                      );
                                    }
                                    return Promise.resolve();
                                  } else {
                                    return addQuestionnaireQuestionTranslation({
                                      translatedDescription: translation.text,
                                      questionnaireQuestionUid:
                                        updatedQuestion.uid,
                                      languageUid: translation.language,
                                    });
                                  }
                                },
                              ),
                            );
                          }

                          // Handle choices and their translations
                          await Promise.all(
                            (question.choices || []).map(
                              async (choice: any) => {
                                const existingChoice =
                                  existingQuestion.choices?.find(
                                    (c) => c.uid === choice.uid,
                                  );

                                if (existingChoice) {
                                  // Update existing choice
                                  const choiceData = prepareUpdateChoiceData(
                                    choice,
                                    existingChoice,
                                  );

                                  const updatedChoice = choiceData
                                    ? await updateQuestionnaireQuestionChoice(
                                        choiceData,
                                      )
                                    : existingChoice;

                                  // Handle choice translations
                                  if (choice.translations) {
                                    const existingChoiceTranslations =
                                      existingChoice.translations || [];

                                    const choiceTranslationsToDelete =
                                      existingChoiceTranslations.filter(
                                        (
                                          // fix the type
                                          t: any,
                                        ) =>
                                          !choice.translations.some(
                                            (newT: { language: string }) =>
                                              newT.language === t.language,
                                          ),
                                      );

                                    await Promise.all(
                                      choiceTranslationsToDelete.map((t) =>
                                        deleteQuestionnaireQuestionChoiceTranslation(
                                          t.uid,
                                        ),
                                      ),
                                    );

                                    // Update or add translations
                                    await Promise.all(
                                      choice.translations.map(
                                        (translation: {
                                          language: string;
                                          text: string;
                                        }) => {
                                          const existingTranslation =
                                            existingChoiceTranslations.find(
                                              (
                                                // fix the type
                                                t: any,
                                              ) =>
                                                t.language ===
                                                translation.language,
                                            );
                                          if (existingTranslation) {
                                            if (
                                              translation.text !==
                                              existingTranslation.text
                                            ) {
                                              return updateQuestionnaireQuestionChoiceTranslation(
                                                {
                                                  uid: existingTranslation.uid,
                                                  translatedDescription:
                                                    translation.text,
                                                  questionnaireQuestionChoiceUid:
                                                    choice.uid,
                                                  languageUid:
                                                    translation.language,
                                                  rowVersion:
                                                    existingTranslation.rowVersion,
                                                },
                                              );
                                            }
                                          } else {
                                            return addQuestionnaireQuestionChoiceTranslation(
                                              {
                                                translatedDescription:
                                                  translation.text,
                                                questionnaireQuestionChoiceUid:
                                                  choice.uid,
                                                languageUid:
                                                  translation.language,
                                              },
                                            );
                                          }
                                        },
                                      ),
                                    );
                                  }
                                } else {
                                  // Create new choice with translations
                                  const choiceData = {
                                    description: choice.description,
                                    weight: choice.weight,
                                    order: choice.order,
                                    questionUid: updatedQuestion.uid,
                                  };
                                  const newChoice =
                                    await addQuestionnaireChoice(choiceData);

                                  // Add translations for new choice
                                  if (choice.translations) {
                                    await Promise.all(
                                      choice.translations.map(
                                        (translation: {
                                          language: string;
                                          text: string;
                                        }) =>
                                          addQuestionnaireQuestionChoiceTranslation(
                                            {
                                              translatedDescription:
                                                translation.text,
                                              questionnaireQuestionChoiceUid:
                                                newChoice.uid,
                                              languageUid: translation.language,
                                            },
                                          ),
                                      ),
                                    );
                                  }
                                }
                              },
                            ),
                          );
                        } else {
                          // Create new question with its choices
                          const questionData = {
                            description: question.description,
                            questionType: question.questionType,
                            minValue:
                              question.questionType === "Numeric"
                                ? question.minValue
                                : undefined,
                            maxValue:
                              question.questionType === "Numeric"
                                ? question.maxValue
                                : undefined,
                            unit:
                              question.questionType === "Numeric"
                                ? question.unit
                                : undefined,
                            isRequired: !!question.isRequired,
                            order: question.order,
                            sectionUid: updatedSection.uid,
                          };
                          const newQuestion =
                            await addQuestionnaireQuestion(questionData);

                          await Promise.all(
                            (question.choices || []).map((choice: any) =>
                              addQuestionnaireChoice({
                                description: choice.description,
                                weight: choice.weight,
                                order: choice.order,
                                questionUid: newQuestion.uid,
                              }),
                            ),
                          );
                        }
                      }),
                    );
                  } else {
                    // Create new section with its questions and choices
                    const sectionData = {
                      name: section.name,
                      description: section.description || "",
                      questionnaireUid: props.initialValues!.uid,
                      order: section.order,
                    };
                    const newSection =
                      await addQuestionnaireSection(sectionData);

                    await Promise.all(
                      (section.questions || []).map(async (question: any) => {
                        const questionData = {
                          description: question.description,
                          questionType: question.questionType,
                          minValue:
                            question.questionType === "Numeric"
                              ? question.minValue
                              : undefined,
                          maxValue:
                            question.questionType === "Numeric"
                              ? question.maxValue
                              : undefined,
                          unit:
                            question.questionType === "Numeric"
                              ? question.unit
                              : undefined,
                          isRequired: !!question.isRequired,
                          order: question.order,
                          sectionUid: newSection.uid,
                        };
                        const newQuestion =
                          await addQuestionnaireQuestion(questionData);

                        await Promise.all(
                          (question.choices || []).map((choice: any) =>
                            addQuestionnaireChoice({
                              description: choice.description,
                              weight: choice.weight,
                              order: choice.order,
                              questionUid: newQuestion.uid,
                            }),
                          ),
                        );
                      }),
                    );
                  }
                }),
              );
            })
            .then(() => {
              props.onFinish?.();
            })
            .catch((error) => {
              setErrorMessage(getProblemMessage(error));
              setError(true);
            })
            .finally(() => {
              setLoading(false);
            });
        });
      } else {
        // Delete
        deleteQuestionnaire(props.initialValues!.uid)
          .then(() => {
            props.onFinish?.();
          })
          .catch((error) => {
            setErrorMessage(getProblemMessage(error));
            setError(true);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  }

  function hideChoices(sectionName: number, questionName: number) {
    // Choiceless types never take choices — also honour the live form value so existing
    // FreeText/Numeric questions hide the Choices panel immediately on edit-load.
    const currentType = form.getFieldValue([
      "sections",
      sectionName,
      "questions",
      questionName,
      "questionType",
    ]);
    if (currentType === "FreeText" || currentType === "Numeric") {
      return true;
    }

    return hiddenChoices.some(
      (choice) =>
        choice.sectionName === sectionName &&
        choice.questionName === questionName,
    );
  }

  // Used in order to hide choices when question type is choiceless (FreeText / Numeric)
  function onQuestionTypeChange(
    sectionName: number,
    questionName: number,
    type: string,
  ) {
    if (type === "FreeText" || type === "Numeric") {
      setHiddenChoices((prevChoices) => [
        ...prevChoices,
        {
          sectionName,
          questionName,
          type,
        },
      ]);
      // Get current form values
      const currentValues = form.getFieldsValue();
      if (currentValues.sections?.[sectionName]?.questions?.[questionName]) {
        currentValues.sections[sectionName].questions[questionName].choices =
          [];
        form.setFieldsValue(currentValues);
      }
    } else {
      // Remove question from hiddenChoices if type is not FreeText
      setHiddenChoices((prevChoices) =>
        prevChoices.filter(
          (choice) =>
            !(
              choice.sectionName === sectionName &&
              choice.questionName === questionName
            ),
        ),
      );
    }
  }

  return (
    <InModal
      open={props.open}
      loading={dataLoad.loading}
      loadingLabel="Loading questionnaire…"
      size={props.mode === "delete" ? "small" : "extraLarge"}
      title={
        (props.readOnly
          ? `Questionnaire: ${props.initialValues?.name ?? ""}`
          : modalTitle(props.mode, "Questionnaire", props.initialValues?.name)) +
        (props.mode !== "add" ? versionLabel(props.initialValues) : "")
      }
      onCancel={props.onCancel}
      actions={
        props.readOnly ? (
          <Button onClick={props.onCancel}>Close</Button>
        ) : (
          <ModalFooter
            mode={props.mode}
            loading={loading}
            valid={valid}
            onCancel={props.onCancel}
            onSubmit={onSubmit}
          />
        )
      }
    >
      {props.readOnly && (
        <Alert
          className="mb-4"
          type="info"
          showIcon
          message={
            props.initialValues?.status === "Retired"
              ? "This version was replaced by a newer one and can no longer be changed. Answers given on it keep it."
              : "This version is published and can no longer be changed. Use New version to make changes; answers already given keep this version."
          }
        />
      )}
      {props.mode !== "delete" ? (
        <Form
          form={form}
          layout="vertical"
          disabled={props.readOnly}
          className="my-form-item flex flex-col pb-4"
        >
          {/* Basic Info section */}
          <Col className="flex flex-col">
            <Form.Item
              name="name"
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Name
                </Typography.Text>
              }
              rules={[{ required: true }]}
              className="m-0 p-1"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="type"
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Type
                </Typography.Text>
              }
              tooltip="Test = a scored assessment that feeds KPIs/reports; Survey = opinion/feedback collection without scoring; Roadmap = a questionnaire that backs a wellbeing-journey (Processes) stage requirement."
              rules={[{ required: true }]}
              className="m-0 p-1"
            >
              <Select placeholder="Select type">
                {QUESTIONNAIRE_TYPES.map((type) => (
                  <Select.Option key={type} value={type}>
                    {type}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="description"
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Description
                </Typography.Text>
              }
              className="m-0 p-1"
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item
              name="duration"
              label={
                <Typography.Text
                  className="text-sm"
                  style={{
                    color: token.colorTextDescription,
                  }}
                >
                  Duration (seconds)
                </Typography.Text>
              }
              tooltip="Estimated time to complete, in seconds (e.g. 600 = 10 minutes). Shown to respondents so they can plan."
              rules={[{ required: true }]}
              className="m-0 p-1"
            >
              <Input type="number" />
            </Form.Item>

            {/* Sections */}
            <Typography.Text
              className="text-sm"
              style={{
                color: token.colorTextDescription,
                marginTop: "0.5rem",
                paddingLeft: "0.5rem",
              }}
            >
              Sections
            </Typography.Text>
            <Form.List name="sections">
              {(sections, { add: addSection, remove: removeSection }) => (
                <Col className="flex flex-col p-1">
                  {sections.map((section, sectionIndex) => (
                    <Collapse
                      key={section.key}
                      className="my-collapse flex flex-col"
                      bordered={false}
                      accordion
                    >
                      <Panel
                        key={section.key}
                        header={`Section ${sectionIndex + 1}`}
                        extra={
                          <Row className="-mb-0.5 -mt-0.5 flex flex-row justify-end">
                            <EditButton locked={props.readOnly}
                              type="text"
                              size="small"
                              disabled={sections.length < 2}
                              onClick={() => removeSection(section.name)}
                            >
                              Remove
                            </EditButton>
                          </Row>
                        }
                      >
                        <Col className="flex flex-col gap-2">
                          <Form.Item
                            hidden
                            name={[section.name, "order"]}
                            initialValue={sectionIndex + 1}
                          >
                            <Input type="number" />
                          </Form.Item>
                          <Form.Item
                            key={`form-item-${section.key}`}
                            name={[section.name, "name"]}
                            label={
                              <Typography.Text
                                className="text-sm"
                                style={{
                                  color: token.colorTextDescription,
                                }}
                              >
                                Name
                              </Typography.Text>
                            }
                            rules={[{ required: true }]}
                            className="m-0 p-1"
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            key={`form-item-desc-${section.key}`}
                            name={[section.name, "description"]}
                            label={
                              <Typography.Text
                                className="text-sm"
                                style={{
                                  color: token.colorTextDescription,
                                }}
                              >
                                Description
                              </Typography.Text>
                            }
                            className="m-0 p-1"
                          >
                            <Input.TextArea />
                          </Form.Item>
                          <Form.Item name={[section.name, "uid"]} hidden>
                            <Input disabled />
                          </Form.Item>

                          {/* Questions */}
                          <Typography.Text
                            className="text-sm"
                            style={{
                              color: token.colorTextDescription,
                              marginTop: "0.5rem",
                              paddingLeft: "0.5rem",
                            }}
                          >
                            Questions
                          </Typography.Text>
                          <Form.List name={[section.name, "questions"]}>
                            {(
                              questions,
                              { add: addQuestion, remove: removeQuestion },
                            ) => (
                              <Col className="flex flex-col p-1">
                                <Collapse
                                  className="my-collapse flex flex-col"
                                  bordered={false}
                                  accordion
                                >
                                  {questions.map((question, questionIndex) => (
                                    <Panel
                                      key={question.key}
                                      header={`Question ${questionIndex + 1}`}
                                      extra={
                                        <Row className="-mb-0.5 -mt-0.5 flex flex-row justify-end">
                                          <EditButton locked={props.readOnly}
                                            type="text"
                                            size="small"
                                            disabled={questions.length < 2}
                                            onClick={() =>
                                              removeQuestion(question.name)
                                            }
                                          >
                                            Remove
                                          </EditButton>
                                        </Row>
                                      }
                                    >
                                      <Col className="flex flex-col">
                                        <Form.Item
                                          key={`form-item-${question.key}`}
                                          name={[question.name, "questionType"]}
                                          label={
                                            <Typography.Text
                                              className="text-sm"
                                              style={{
                                                color:
                                                  token.colorTextDescription,
                                              }}
                                            >
                                              Question Type
                                            </Typography.Text>
                                          }
                                          tooltip="How respondents answer: single/multiple choice, a scale, free text, etc. This controls whether answer choices (and their weights) apply."
                                          rules={[{ required: true }]}
                                          className="m-0 p-1"
                                        >
                                          <Select
                                            onChange={(value) => {
                                              onQuestionTypeChange(
                                                section.name,
                                                question.name,
                                                value,
                                              );
                                            }}
                                            placeholder="Select question type"
                                          >
                                            {QUESTION_TYPES.map((type) => (
                                              <Select.Option
                                                key={type}
                                                value={type}
                                              >
                                                {QUESTION_TYPE_LABELS[type]}
                                              </Select.Option>
                                            ))}
                                          </Select>
                                        </Form.Item>
                                        <Form.Item
                                          hidden
                                          key={`form-item-order-${question.key}`}
                                          name={[question.name, "order"]}
                                          label={
                                            <Typography.Text
                                              className="text-sm"
                                              style={{
                                                color:
                                                  token.colorTextDescription,
                                              }}
                                            >
                                              Order
                                            </Typography.Text>
                                          }
                                          initialValue={questionIndex + 1}
                                          className="m-0 p-1"
                                        >
                                          <Input
                                            type="number"
                                            disabled={true}
                                          />
                                        </Form.Item>
                                        <Form.Item
                                          key={`form-item-type-${question.key}`}
                                          name={[question.name, "description"]}
                                          label={
                                            <Typography.Text
                                              className="text-sm"
                                              style={{
                                                color:
                                                  token.colorTextDescription,
                                              }}
                                            >
                                              Description
                                            </Typography.Text>
                                          }
                                          rules={[{ required: true }]}
                                          className="m-0 p-1"
                                        >
                                          <Input.TextArea />
                                        </Form.Item>

                                        {/* Numeric-question bounds + unit (advisory: drive the answer input). */}
                                        <Form.Item
                                          noStyle
                                          shouldUpdate={(prev, cur) =>
                                            prev.sections?.[section.name]
                                              ?.questions?.[question.name]
                                              ?.questionType !==
                                            cur.sections?.[section.name]
                                              ?.questions?.[question.name]
                                              ?.questionType
                                          }
                                        >
                                          {() =>
                                            form.getFieldValue([
                                              "sections",
                                              section.name,
                                              "questions",
                                              question.name,
                                              "questionType",
                                            ]) === "Numeric" ? (
                                              <Row className="gap-2 px-1">
                                                <Form.Item
                                                  name={[
                                                    question.name,
                                                    "minValue",
                                                  ]}
                                                  label={
                                                    <Typography.Text
                                                      className="text-sm"
                                                      style={{
                                                        color:
                                                          token.colorTextDescription,
                                                      }}
                                                    >
                                                      Min
                                                    </Typography.Text>
                                                  }
                                                  className="m-0 flex-1"
                                                >
                                                  <InputNumber className="w-full" />
                                                </Form.Item>
                                                <Form.Item
                                                  name={[
                                                    question.name,
                                                    "maxValue",
                                                  ]}
                                                  label={
                                                    <Typography.Text
                                                      className="text-sm"
                                                      style={{
                                                        color:
                                                          token.colorTextDescription,
                                                      }}
                                                    >
                                                      Max
                                                    </Typography.Text>
                                                  }
                                                  className="m-0 flex-1"
                                                >
                                                  <InputNumber className="w-full" />
                                                </Form.Item>
                                                <Form.Item
                                                  name={[question.name, "unit"]}
                                                  label={
                                                    <Typography.Text
                                                      className="text-sm"
                                                      style={{
                                                        color:
                                                          token.colorTextDescription,
                                                      }}
                                                    >
                                                      Unit
                                                    </Typography.Text>
                                                  }
                                                  className="m-0 flex-1"
                                                >
                                                  <Input placeholder="e.g. %, days" />
                                                </Form.Item>
                                              </Row>
                                            ) : null
                                          }
                                        </Form.Item>

                                        {/* Required: blocks questionnaire completion until answered. */}
                                        <Form.Item
                                          name={[question.name, "isRequired"]}
                                          valuePropName="checked"
                                          initialValue={false}
                                          label={
                                            <Typography.Text
                                              className="text-sm"
                                              style={{
                                                color: token.colorTextDescription,
                                              }}
                                            >
                                              Required
                                            </Typography.Text>
                                          }
                                          tooltip="When on, respondents cannot complete the questionnaire until this question is answered. Questions that feed a banded (assessment) KPI are always required, whatever this says — a gap would falsify the band. For data-collection questions (count KPIs), leave it off to let a category stay empty (it then means nothing to report), or switch it on to demand an answer."
                                          className="m-0 p-1"
                                        >
                                          <Switch size="small" />
                                        </Form.Item>

                                        {/* Add Translations section for Questions */}
                                        <Collapse
                                          className="my-collapse flex flex-col"
                                          bordered={false}
                                        >
                                          <Panel
                                            key="translations-panel"
                                            header={
                                              <Typography.Text
                                                className="text-sm"
                                                style={{
                                                  color:
                                                    token.colorTextDescription,
                                                }}
                                              >
                                                Translations
                                              </Typography.Text>
                                            }
                                          >
                                            <Form.List
                                              name={[
                                                question.name,
                                                "translations",
                                              ]}
                                            >
                                              {(
                                                translations,
                                                {
                                                  add: addTranslation,
                                                  remove: removeTranslation,
                                                },
                                              ) => (
                                                <Col className="flex flex-col p-1">
                                                  {translations.map(
                                                    (translation, index) => (
                                                      <Row
                                                        key={translation.key}
                                                        className="mb-2 flex items-center gap-2"
                                                      >
                                                        <Form.Item
                                                          name={[
                                                            translation.name,
                                                            "language",
                                                          ]}
                                                          className="mb-0"
                                                        >
                                                          <Select placeholder="Select language">
                                                            {languages.map(
                                                              (lang) => (
                                                                <Select.Option
                                                                  key={lang.uid}
                                                                  value={
                                                                    lang.uid
                                                                  }
                                                                >
                                                                  {lang.name}
                                                                </Select.Option>
                                                              ),
                                                            )}
                                                          </Select>
                                                        </Form.Item>
                                                        <Form.Item
                                                          name={[
                                                            translation.name,
                                                            "text",
                                                          ]}
                                                          className="mb-0 flex-1"
                                                        >
                                                          <Input.TextArea placeholder="Translated description" />
                                                        </Form.Item>
                                                        <EditButton locked={props.readOnly}
                                                          type="text"
                                                          onClick={() =>
                                                            removeTranslation(
                                                              translation.name,
                                                            )
                                                          }
                                                          style={{
                                                            padding: "0 8px",
                                                          }}
                                                        >
                                                          Remove
                                                        </EditButton>
                                                      </Row>
                                                    ),
                                                  )}
                                                  <EditButton locked={props.readOnly}
                                                    type="dashed"
                                                    onClick={() =>
                                                      addTranslation()
                                                    }
                                                    block
                                                  >
                                                    Add Translation
                                                  </EditButton>
                                                </Col>
                                              )}
                                            </Form.List>
                                          </Panel>
                                        </Collapse>

                                        <Form.Item
                                          name={[question.name, "uid"]}
                                          hidden
                                        >
                                          <Input disabled />
                                        </Form.Item>

                                        {/* Choices section */}
                                        {hideChoices(
                                          section.name,
                                          question.name,
                                        ) ? null : (
                                          <Collapse
                                            className="my-collapse flex flex-col"
                                            bordered={false}
                                          >
                                            <Panel
                                              key="choices-panel"
                                              header={
                                                <Typography.Text
                                                  className="text-sm"
                                                  style={{
                                                    color:
                                                      token.colorTextDescription,
                                                  }}
                                                >
                                                  Choices
                                                </Typography.Text>
                                              }
                                            >
                                              <Form.List
                                                name={[
                                                  question.name,
                                                  "choices",
                                                ]}
                                              >
                                                {(
                                                  choices,
                                                  {
                                                    add: addChoice,
                                                    remove: removeChoice,
                                                  },
                                                ) => (
                                                  <Col className="flex flex-col p-1">
                                                    <Collapse
                                                      className="my-collapse flex flex-col"
                                                      bordered={false}
                                                      accordion
                                                    >
                                                      {choices.map(
                                                        (
                                                          choice,
                                                          choiceIndex,
                                                        ) => (
                                                          <Panel
                                                            key={choice.key}
                                                            header={`Choice ${choiceIndex + 1}`}
                                                            extra={
                                                              <Row className="-mb-0.5 -mt-0.5 flex flex-row justify-end">
                                                                <EditButton locked={props.readOnly}
                                                                  type="text"
                                                                  size="small"
                                                                  disabled={
                                                                    choices.length <
                                                                    2
                                                                  }
                                                                  onClick={() =>
                                                                    removeChoice(
                                                                      choice.name,
                                                                    )
                                                                  }
                                                                >
                                                                  Remove
                                                                </EditButton>
                                                              </Row>
                                                            }
                                                          >
                                                            <Form.Item
                                                              key={`form-item-${choice.key}`}
                                                              name={[
                                                                choice.name,
                                                                "description",
                                                              ]}
                                                              label={
                                                                <Typography.Text
                                                                  className="text-sm"
                                                                  style={{
                                                                    color:
                                                                      token.colorTextDescription,
                                                                  }}
                                                                >
                                                                  Description
                                                                </Typography.Text>
                                                              }
                                                              rules={[
                                                                {
                                                                  required:
                                                                    true,
                                                                },
                                                              ]}
                                                              className="m-0 p-1"
                                                            >
                                                              <Input.TextArea />
                                                            </Form.Item>
                                                            <Form.Item
                                                              hidden
                                                              key={`form-item-order-${choice.key}`}
                                                              name={[
                                                                choice.name,
                                                                "order",
                                                              ]}
                                                              label={
                                                                <Typography.Text
                                                                  className="text-sm"
                                                                  style={{
                                                                    color:
                                                                      token.colorTextDescription,
                                                                  }}
                                                                >
                                                                  Order
                                                                </Typography.Text>
                                                              }
                                                              initialValue={
                                                                choiceIndex + 1
                                                              }
                                                              className="m-0 p-1"
                                                            >
                                                              <Input
                                                                type="number"
                                                                disabled={true}
                                                              />
                                                            </Form.Item>
                                                            <Form.Item
                                                              key={`form-item-weight-${choice.key}`}
                                                              name={[
                                                                choice.name,
                                                                "weight",
                                                              ]}
                                                              label={
                                                                <Typography.Text
                                                                  className="text-sm"
                                                                  style={{
                                                                    color:
                                                                      token.colorTextDescription,
                                                                  }}
                                                                >
                                                                  Weight
                                                                </Typography.Text>
                                                              }
                                                              tooltip="Numeric score this choice contributes when selected (e.g. 1–5 on a Likert scale). KPIs sum or average these weights."
                                                              className="m-0 p-1"
                                                            >
                                                              <Input type="number" />
                                                            </Form.Item>
                                                            <Form.Item
                                                              name={[
                                                                choice.name,
                                                                "uid",
                                                              ]}
                                                              hidden
                                                            >
                                                              <Input disabled />
                                                            </Form.Item>

                                                            {/* Choice Translations */}
                                                            <Collapse
                                                              className="my-collapse flex flex-col"
                                                              bordered={false}
                                                            >
                                                              <Panel
                                                                key="choice-translations-panel"
                                                                header={
                                                                  <Typography.Text
                                                                    className="text-sm"
                                                                    style={{
                                                                      color:
                                                                        token.colorTextDescription,
                                                                    }}
                                                                  >
                                                                    Translations
                                                                  </Typography.Text>
                                                                }
                                                              >
                                                                <Form.List
                                                                  name={[
                                                                    choice.name,
                                                                    "translations",
                                                                  ]}
                                                                >
                                                                  {(
                                                                    translations,
                                                                    {
                                                                      add: addTranslation,
                                                                      remove:
                                                                        removeTranslation,
                                                                    },
                                                                  ) => (
                                                                    <Col className="flex flex-col p-1">
                                                                      {translations.map(
                                                                        (
                                                                          translation,
                                                                          index,
                                                                        ) => (
                                                                          <Row
                                                                            key={
                                                                              translation.key
                                                                            }
                                                                            className="mb-2 flex items-center gap-2"
                                                                          >
                                                                            <Form.Item
                                                                              name={[
                                                                                translation.name,
                                                                                "language",
                                                                              ]}
                                                                              className="mb-0"
                                                                              style={{
                                                                                width:
                                                                                  "15%",
                                                                              }}
                                                                            >
                                                                              <Select placeholder="Select language">
                                                                                {languages.map(
                                                                                  (
                                                                                    lang,
                                                                                  ) => (
                                                                                    <Select.Option
                                                                                      key={
                                                                                        lang.uid
                                                                                      }
                                                                                      value={
                                                                                        lang.uid
                                                                                      }
                                                                                    >
                                                                                      {
                                                                                        lang.name
                                                                                      }
                                                                                    </Select.Option>
                                                                                  ),
                                                                                )}
                                                                              </Select>
                                                                            </Form.Item>
                                                                            <Form.Item
                                                                              name={[
                                                                                translation.name,
                                                                                "text",
                                                                              ]}
                                                                              className="mb-0 flex-1"
                                                                            >
                                                                              <Input.TextArea placeholder="Translated description" />
                                                                            </Form.Item>
                                                                            <EditButton locked={props.readOnly}
                                                                              type="text"
                                                                              onClick={() =>
                                                                                removeTranslation(
                                                                                  translation.name,
                                                                                )
                                                                              }
                                                                              style={{
                                                                                padding:
                                                                                  "0 8px",
                                                                              }}
                                                                            >
                                                                              Remove
                                                                            </EditButton>
                                                                          </Row>
                                                                        ),
                                                                      )}
                                                                      <EditButton locked={props.readOnly}
                                                                        type="dashed"
                                                                        onClick={() =>
                                                                          addTranslation()
                                                                        }
                                                                        block
                                                                      >
                                                                        Add
                                                                        Translation
                                                                      </EditButton>
                                                                    </Col>
                                                                  )}
                                                                </Form.List>
                                                              </Panel>
                                                            </Collapse>
                                                          </Panel>
                                                        ),
                                                      )}
                                                    </Collapse>
                                                    <Row className="mt-2 flex flex-row justify-start pl-2 pr-2">
                                                      <EditButton locked={props.readOnly}
                                                        type="dashed"
                                                        onClick={() =>
                                                          addChoice()
                                                        }
                                                        block
                                                      >
                                                        Add Choice
                                                      </EditButton>
                                                    </Row>
                                                  </Col>
                                                )}
                                              </Form.List>
                                            </Panel>
                                          </Collapse>
                                        )}
                                      </Col>
                                    </Panel>
                                  ))}
                                </Collapse>
                                <Row className="mt-2 flex flex-row justify-start pl-2 pr-2">
                                  <EditButton locked={props.readOnly} onClick={() => addQuestion()}>
                                    Add Question
                                  </EditButton>
                                </Row>
                              </Col>
                            )}
                          </Form.List>
                        </Col>
                      </Panel>
                    </Collapse>
                  ))}
                  <Row className="mt-2 flex flex-row justify-start pl-2 pr-2">
                    <EditButton locked={props.readOnly} onClick={() => addSection()}>Add Section</EditButton>
                  </Row>
                </Col>
              )}
            </Form.List>
          </Col>
        </Form>
      ) : (
        <ModalDeleteConfirmation
          entityLabel="questionnaire"
          entityName={props.initialValues?.name}
        />
      )}

      {error && <ModalErrorAlert message={errorMessage} />}
    </InModal>
  );
}

export default QuestionnairesFormDialog;
